import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import * as React from "react";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

/**
 * Reports, per part of a dialog's content, whether it holds changes not yet saved. Keyed so that a
 * form built from several components can report from each, and a part that unmounts takes its
 * report with it.
 */
type ReportChanges = (key: object, changed: boolean) => void;

const DiscardGuardContext = React.createContext<ReportChanges | null>(null);

/**
 * A dialog that asks before throwing away unsaved changes.
 *
 * Esc, a click outside and the close button all reach Radix's `onOpenChange(false)`. While a form
 * inside has said it holds changes (`useDiscardGuard`), that request is held and "Discard your
 * changes?" asked instead. With nothing changed, or once the changes are saved, the dialog closes as
 * before. A form's own Cancel button and its close after a save call the page's `onOpenChange`
 * directly, so they never ask: pressing Cancel is already the choice to discard.
 */
export function Dialog({
  open: openProp, defaultOpen, onOpenChange, children, ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false);
  const open = openProp ?? uncontrolledOpen;
  const reports = React.useRef(new Map<object, boolean>());
  const [changed, setChanged] = React.useState(false);
  const [asking, setAsking] = React.useState(false);
  // Where somebody who keeps editing is put back: the field they were in when they pressed Esc, or
  // `null` for the dialog itself. Radix left focus on the page behind, so more typing went nowhere.
  // `undefined` once they discard, when the dialog closes and Radix returns focus as usual.
  const resumeAt = React.useRef<HTMLElement | null | undefined>(undefined);

  const report = React.useCallback<ReportChanges>((key, isChanged) => {
    if (isChanged) reports.current.set(key, true);
    else reports.current.delete(key);
    setChanged(reports.current.size > 0);
  }, []);

  const setOpen = (next: boolean) => {
    if (openProp === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && changed) {
          const active = document.activeElement;
          resumeAt.current = active instanceof HTMLElement && active !== document.body ? active : null;
          setAsking(true);
        } else {
          setOpen(next);
        }
      }}
      {...props}
    >
      <DiscardGuardContext.Provider value={report}>{children}</DiscardGuardContext.Provider>
      <ConfirmDialog
        open={asking}
        onOpenChange={setAsking}
        title="Discard your changes?"
        description="You have changes that are not saved. Closing this window loses them."
        cancelLabel="Keep editing"
        confirmLabel="Discard changes"
        destructive
        onConfirm={() => {
          resumeAt.current = undefined;
          setAsking(false);
          setOpen(false);
        }}
        onCloseAutoFocus={(event) => {
          const field = resumeAt.current;
          resumeAt.current = undefined;
          if (field === undefined) return;
          // A click outside leaves nothing focused; the topmost dialog, the one kept open, is next best.
          const dialogs = document.querySelectorAll<HTMLElement>("[role=dialog]");
          const target = field?.isConnected ? field : dialogs[dialogs.length - 1];
          if (!target) return;
          event.preventDefault();
          target.focus();
        }}
      />
    </DialogPrimitive.Root>
  );
}

/**
 * Tells the dialog around this component whether it holds changes not yet saved, so closing it by
 * Esc, a click outside or the close button asks first. `changed` should be false again once the
 * changes are saved. Outside a `<Dialog>` it does nothing.
 */
export function useDiscardGuard(changed: boolean) {
  const report = React.useContext(DiscardGuardContext);
  const [key] = React.useState(() => ({}));
  React.useEffect(() => {
    report?.(key, changed);
  }, [report, key, changed]);
  React.useEffect(() => () => report?.(key, false), [report, key]);
}

/** `useDiscardGuard` as an element, for a dialog whose form state lives in the component that renders
 *  the `<Dialog>` itself, above the context the hook reads: put it anywhere inside the content. */
export function DiscardGuard({ changed }: { changed: boolean }) {
  useDiscardGuard(changed);
  return null;
}

export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

/**
 * `center` is a classic modal, for confirmations and single facts. `drawer` is a full-height
 * panel that slides in from the right — the shape every edit form uses, because a form that is
 * taller than a centred box can be is the norm here and a drawer gives it the screen's whole
 * height instead of scrolling a short window.
 */
type DialogVariant = "center" | "drawer";

/** The header has to clear the close control, which is wider in a drawer (it carries an `esc`
 *  hint). Every call site renders the same `<DialogHeader>` and none of them should have to know
 *  that, so the variant travels down here rather than through a prop on each one. */
const DialogVariantContext = React.createContext<DialogVariant>("center");

export function DialogContent({
  className, children, size = "md", variant = "center", ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  size?: "sm" | "md" | "lg";
  variant?: DialogVariant;
}) {
  const drawer = variant === "drawer";
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn(
          "fixed inset-0 z-50 bg-gray-12/25 backdrop-blur-[1px]",
          "data-[state=open]:animate-overlay-in data-[state=closed]:animate-overlay-out",
        )}
      />
      <DialogPrimitive.Content
        className={cn(
          "fixed z-50 flex flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-lg",
          drawer
            ? [
                // `inset-y-2 right-2` is the full height of the viewport less an 8px margin, so
                // the panel keeps its rounded corners and reads as a sheet over the app rather
                // than a second window edge butted against the first.
                "inset-y-2 right-2 w-[calc(100vw-1rem)]",
                "data-[state=open]:animate-drawer-in data-[state=closed]:animate-drawer-out",
              ]
            : [
                "left-1/2 top-1/2 max-h-[calc(100dvh-4rem)] w-[calc(100vw-2rem)]",
                "-translate-x-1/2 -translate-y-1/2",
                "data-[state=open]:animate-dialog-in data-[state=closed]:animate-dialog-out",
              ],
          { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl" }[size],
          className,
        )}
        {...props}
      >
        <DialogVariantContext.Provider value={variant}>{children}</DialogVariantContext.Provider>

        <div className="absolute right-3 top-3 flex items-center gap-1.5">
          {drawer && (
            <kbd
              aria-hidden
              className={cn(
                "hidden rounded border border-border bg-surface-sunken px-1.5 py-0.5",
                "font-sans text-[11px] leading-none text-text-subtle sm:block",
              )}
            >
              esc
            </kbd>
          )}
          <DialogPrimitive.Close
            className={cn(
              "rounded p-1 text-text-subtle transition-colors",
              "hover:bg-surface-hover hover:text-text",
            )}
          >
            <X className="size-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const drawer = React.useContext(DialogVariantContext) === "drawer";
  return (
    <div
      className={cn(
        "shrink-0 space-y-1 border-b border-border px-5 py-4",
        drawer ? "pr-20" : "pr-12",
        className,
      )}
      {...props}
    />
  );
}

/** Scrolls; the header and footer stay put. Long forms are the norm here. */
export function DialogBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("min-h-0 flex-1 overflow-y-auto px-5 py-4 scrollbar-subtle", className)} {...props} />;
}

/** Wraps a body and footer that sit inside a `<form>`: the form is a flex child of the panel and
 *  has to pass the column through, or the body never scrolls and the footer floats up the panel. */
export function DialogForm({ className, ...props }: React.FormHTMLAttributes<HTMLFormElement>) {
  return <form className={cn("flex min-h-0 flex-1 flex-col", className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "shrink-0 flex flex-col-reverse gap-2 border-t border-border bg-gray-2 px-5 py-3",
        "sm:flex-row sm:justify-end dark:bg-gray-1",
        className,
      )}
      {...props}
    />
  );
}

export function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title className={cn("text-lg font-semibold text-text", className)} {...props} />;
}

export function DialogDescription({
  className, ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return <DialogPrimitive.Description className={cn("text-sm text-text-muted", className)} {...props} />;
}
